import React, { useState, useEffect } from 'react';
import { useLanguage, api, COUNTRIES } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Bell, Globe, Briefcase, Mail, Loader2, AlertCircle, 
  CheckCircle2, Crown, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

// Job categories
const JOB_CATEGORIES = [
  'construction', 'logistics', 'manufacturing', 'hospitality', 
  'cleaning', 'agriculture', 'healthcare', 'it', 'retail', 'other'
];

export const NotificationPreferences = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState({
    countries: [],
    job_categories: [],
    job_types: [],
    email_enabled: true,
    frequency: 'instant',
    free_notifications_used: 0,
    free_notifications_limit: 3,
    notifications_remaining: 3,
    subscription_required: false
  });

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const res = await api.getNotificationPrefs();
      setPrefs(res.data);
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateNotificationPrefs({
        countries: prefs.countries,
        job_categories: prefs.job_categories,
        job_types: prefs.job_types,
        email_enabled: prefs.email_enabled,
        frequency: prefs.frequency
      });
      toast.success(t('notifications.preferencesSaved'));
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const toggleCountry = (code) => {
    setPrefs(prev => ({
      ...prev,
      countries: prev.countries.includes(code)
        ? prev.countries.filter(c => c !== code)
        : [...prev.countries, code]
    }));
  };

  const toggleCategory = (cat) => {
    setPrefs(prev => ({
      ...prev,
      job_categories: prev.job_categories.includes(cat)
        ? prev.job_categories.filter(c => c !== cat)
        : [...prev.job_categories, cat]
    }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const freeNotificationsPercent = (prefs.free_notifications_used / prefs.free_notifications_limit) * 100;
  const isUnlimited = prefs.notifications_remaining === -1;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          {t('notifications.jobAlerts')}
        </CardTitle>
        <CardDescription>
          {t('notifications.alertsDesc')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Usage Status */}
        <div className={`rounded-lg p-4 ${prefs.subscription_required ? 'bg-amber-50 dark:bg-amber-950/30' : 'bg-green-50 dark:bg-green-950/30'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium flex items-center gap-2">
              {isUnlimited ? (
                <>
                  <Crown className="h-4 w-4 text-amber-500" />
                  {t('notifications.unlimited')}
                </>
              ) : prefs.subscription_required ? (
                <>
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  {t('notifications.limitReached')}
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  {t('notifications.freeRemaining')}
                </>
              )}
            </span>
            {!isUnlimited && (
              <Badge variant={prefs.subscription_required ? 'secondary' : 'default'}>
                {prefs.notifications_remaining} / {prefs.free_notifications_limit}
              </Badge>
            )}
          </div>
          {!isUnlimited && (
            <Progress value={freeNotificationsPercent} className="h-2" />
          )}
          {prefs.subscription_required && (
            <p className="text-sm text-amber-700 dark:text-amber-400 mt-2">
              {t('notifications.subscribeToReceiveMore')}
            </p>
          )}
        </div>

        {/* Email Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail className="h-5 w-5 text-muted-foreground" />
            <div>
              <Label>{t('notifications.emailAlerts')}</Label>
              <p className="text-xs text-muted-foreground">
                {t('notifications.emailAlertsDesc')}
              </p>
            </div>
          </div>
          <Switch
            checked={prefs.email_enabled}
            onCheckedChange={(checked) => setPrefs({ ...prefs, email_enabled: checked })}
            data-testid="email-alerts-toggle"
          />
        </div>

        {/* Countries */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            {t('notifications.countries')}
          </Label>
          <p className="text-xs text-muted-foreground mb-2">
            {t('notifications.countriesDesc')}
          </p>
          <div className="flex flex-wrap gap-2">
            {COUNTRIES.map((country) => (
              <Badge
                key={country.code}
                variant={prefs.countries.includes(country.code) ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => toggleCountry(country.code)}
                data-testid={`country-${country.code}`}
              >
                {t(country.nameKey)}
              </Badge>
            ))}
          </div>
          {prefs.countries.length === 0 && (
            <p className="text-xs text-muted-foreground italic">
              {t('notifications.allCountries')}
            </p>
          )}
        </div>

        {/* Job Categories */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Briefcase className="h-4 w-4" />
            {t('notifications.categories')}
          </Label>
          <p className="text-xs text-muted-foreground mb-2">
            {t('notifications.categoriesDesc')}
          </p>
          <div className="flex flex-wrap gap-2">
            {JOB_CATEGORIES.map((cat) => (
              <Badge
                key={cat}
                variant={prefs.job_categories.includes(cat) ? 'default' : 'outline'}
                className="cursor-pointer capitalize"
                onClick={() => toggleCategory(cat)}
                data-testid={`category-${cat}`}
              >
                {t(`categories.${cat}`) || cat}
              </Badge>
            ))}
          </div>
          {prefs.job_categories.length === 0 && (
            <p className="text-xs text-muted-foreground italic">
              {t('notifications.allCategories')}
            </p>
          )}
        </div>

        {/* Frequency */}
        <div className="space-y-2">
          <Label>{t('notifications.frequency')}</Label>
          <Select 
            value={prefs.frequency} 
            onValueChange={(v) => setPrefs({ ...prefs, frequency: v })}
          >
            <SelectTrigger data-testid="frequency-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="instant">{t('notifications.instant')}</SelectItem>
              <SelectItem value="daily_digest">{t('notifications.dailyDigest')}</SelectItem>
              <SelectItem value="weekly_digest">{t('notifications.weeklyDigest')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent>
      <CardFooter>
        <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-prefs-btn">
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('common.saving')}
            </>
          ) : (
            <>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              {t('common.save')}
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default NotificationPreferences;
